import { formatOptionLabel } from "@src/core/engine/helpers/fieldDefinitions/getOptionsFieldDefinition";
import {
  WaterCharacteristicsDefinitions,
  waterCharacteristicsRepository,
} from "@src/core/engine/technicalParameters/waterCharacteristics/waterCharacteristicsRepository";
import {
  isZodValidationResult,
  useParseZodValidationResult,
} from "@src/core/engine/validationResults/useParseZodValidationResult";
import { useStore } from "@tanstack/react-form";
import { useTranslation } from "react-i18next";
import { useWaterCharacteristicsForm } from "../hooks/useWaterCharacteristicsForm";
import styles from "./ExistingAdditivesForm.module.css";
import { WaterInformationDescription } from "./WaterInformationDescription";

type ExistingAdditivesFormProps = {
  definitions: WaterCharacteristicsDefinitions;
  form: ReturnType<typeof useWaterCharacteristicsForm>["form"];
};

export function ExistingAdditivesForm({
  definitions,
  form,
}: ExistingAdditivesFormProps) {
  const { t } = useTranslation("settings");
  const parseZodValidationResult = useParseZodValidationResult({
    fieldsT: {
      prefix: "technicalPreferences.waterCharacteristics",
      t,
    },
  });

  const waterInformation = useStore(form.store, (state) =>
    waterCharacteristicsRepository.getWaterInformation(
      state.values.mode === "predefined" ? state.values : undefined,
    ),
  );

  const additive = useStore(form.store, (state) =>
    state.values.mode === "predefined" ? state.values.additives : null,
  );
  const shouldUseWeight = !!additive && additive !== "WATER";

  return (
    <>
      <fieldset className={styles.ExistingAdditivesForm}>
        <form.AppField name="additives">
          {(field) => (
            <field.Select
              errors={field.state.meta.errors?.map((error) =>
                typeof error === "string"
                  ? isZodValidationResult(error)
                    ? parseZodValidationResult(error)
                    : t(error)
                  : undefined,
              )}
              label={t(
                "technicalPreferences.waterCharacteristics.fields.additives.label",
              )}
            >
              {definitions.additives.groups.map((additiveFamily) => (
                <optgroup
                  key={additiveFamily.id}
                  label={formatOptionLabel(additiveFamily, t)}
                >
                  {additiveFamily.options.map((additive) => (
                    <option key={additive.id} value={additive.id}>
                      {formatOptionLabel(additive, t)}{" "}
                      {additive.default ? "(default)" : ""}
                    </option>
                  ))}
                </optgroup>
              ))}
            </field.Select>
          )}
        </form.AppField>

        {shouldUseWeight && (
          <form.AppField name="weight">
            {(field) => {
              return (
                <field.NumberField
                  definition={definitions.weight}
                  errors={field.state.meta.errors?.map((error) =>
                    typeof error === "string"
                      ? isZodValidationResult(error)
                        ? parseZodValidationResult(error)
                        : t(error)
                      : undefined,
                  )}
                  label={t(
                    "technicalPreferences.waterCharacteristics.fields.weight.label",
                  )}
                  placeholder={t(
                    "technicalPreferences.waterCharacteristics.fields.weight.placeholder",
                  )}
                  suffix="%"
                />
              );
            }}
          </form.AppField>
        )}

        <form.AppField name="supplyTemperature">
          {(field) => (
            <field.NumberField
              definition={definitions.supplyTemperature}
              errors={field.state.meta.errors?.map((error) =>
                typeof error === "string"
                  ? isZodValidationResult(error)
                    ? parseZodValidationResult(error)
                    : t(error)
                  : undefined,
              )}
              label={t(
                "technicalPreferences.waterCharacteristics.fields.supplyTemperature.label",
              )}
              placeholder={t(
                "technicalPreferences.waterCharacteristics.fields.supplyTemperature.placeholder",
              )}
            />
          )}
        </form.AppField>

        <form.AppField name="defaultDifferentialTemperature">
          {(field) => (
            <field.NumberField
              definition={definitions.defaultDifferentialTemperature}
              errors={field.state.meta.errors?.map((error) =>
                typeof error === "string"
                  ? isZodValidationResult(error)
                    ? parseZodValidationResult(error)
                    : t(error)
                  : undefined,
              )}
              label={t(
                "technicalPreferences.waterCharacteristics.fields.defaultDifferentialTemperature.label",
              )}
              placeholder={t(
                "technicalPreferences.waterCharacteristics.fields.defaultDifferentialTemperature.placeholder",
              )}
            />
          )}
        </form.AppField>
      </fieldset>

      <fieldset className={styles.WaterInformation}>
        <table>
          <caption>
            {t("technicalPreferences.waterCharacteristics.resultsCaption")}
          </caption>
          <tbody>
            {waterInformation?.status === "success" && (
              <>
                <WaterInformationDescription
                  field={waterInformation.data.density}
                />
                <WaterInformationDescription
                  field={waterInformation.data.freezingPoint}
                />
                <WaterInformationDescription
                  field={waterInformation.data.kinematicViscosity}
                />
                <WaterInformationDescription
                  field={waterInformation.data.specificHeat}
                />
              </>
            )}
          </tbody>
        </table>
      </fieldset>
    </>
  );
}
